import noop from 'lodash/noop';
import React, {
  createContext,
  useContext,
  useRef,
  useCallback,
  useEffect,
  useState,
  ReactNode
} from 'react';

import { Panel } from '@mpm-druid/types';

export type PanelSubmitAction = 'create' | 'update';

export interface SubmitPanelPayload {
  panel: Panel;
  action: PanelSubmitAction;
}

export type SubmitPanelResult =
  | { type: 'cancel' }
  | { type: 'confirm' }
  | { type: 'replace'; payload: SubmitPanelPayload };

export type SubmitPanelInterceptor = (
  payload: SubmitPanelPayload
) => Promise<SubmitPanelResult>;

export type SubmitPanelListener = (payload: SubmitPanelPayload) => void;

interface SubmitPanelContextType {
  registerPanelInterceptor: (fn: SubmitPanelInterceptor) => () => void;
  registerSubmitPanelListener: (fn: SubmitPanelListener) => () => void;
  submitPanel: (panel: Panel, action: PanelSubmitAction) => void;
}

const SubmitPanelContext = createContext<SubmitPanelContextType>({
  registerPanelInterceptor: () => noop,
  registerSubmitPanelListener: () => noop,
  submitPanel: noop
});

export function SubmitPanelProvider({ children }: { children: ReactNode }) {
  const interceptors = useRef<SubmitPanelInterceptor[]>([]);
  const listeners = useRef<SubmitPanelListener[]>([]);

  const registerPanelInterceptor = useCallback((fn: SubmitPanelInterceptor) => {
    interceptors.current.push(fn);

    return () => {
      interceptors.current = interceptors.current.filter(f => f !== fn);
    };
  }, []);

  const registerSubmitPanelListener = useCallback((fn: SubmitPanelListener) => {
    listeners.current.push(fn);

    return () => {
      listeners.current = listeners.current.filter(l => l !== fn);
    };
  }, []);

  const submitPanel = useCallback(
    async (panel: Panel, action: PanelSubmitAction) => {
      let current: SubmitPanelPayload = { panel, action };

      for (const interceptor of interceptors.current) {
        const result = await interceptor(current);

        if (result.type === 'cancel') {
          return;
        }
        if (result.type === 'replace') {
          current = result.payload;
        }
      }

      listeners.current.forEach(fn => fn(current));
    },
    []
  );

  return (
    <SubmitPanelContext.Provider
      value={{
        registerPanelInterceptor,
        registerSubmitPanelListener,
        submitPanel
      }}
    >
      {children}
    </SubmitPanelContext.Provider>
  );
}

export function useSubmitPanelContext() {
  return useContext(SubmitPanelContext);
}

export function useSubmitPanelTrigger() {
  const { submitPanel } = useSubmitPanelContext();
  return submitPanel;
}

export function useSubmitPanelListener(callback: SubmitPanelListener) {
  const { registerSubmitPanelListener } = useSubmitPanelContext();

  useEffect(() => {
    // It automatically unregisters the listener when the component unmounts
    return registerSubmitPanelListener(callback);
  }, [registerSubmitPanelListener, callback]);
}

export function useSubmitPanelInterceptor(
  shouldIntercept: (payload: SubmitPanelPayload) => boolean
) {
  const { registerPanelInterceptor } = useSubmitPanelContext();

  const [isIntercepting, setIsIntercepting] = useState(false);
  const payloadRef = useRef<SubmitPanelPayload | null>(null);
  const resolveRef = useRef<(res: SubmitPanelResult) => void>();

  useEffect(() => {
    const interceptor: SubmitPanelInterceptor = p => {
      if (!shouldIntercept(p)) {
        return Promise.resolve<SubmitPanelResult>({ type: 'confirm' });
      }

      return new Promise<SubmitPanelResult>(resolve => {
        resolveRef.current = resolve;
        payloadRef.current = p;
        setIsIntercepting(true);
      });
    };

    // It automatically unregisters the interceptor when the component unmounts
    return registerPanelInterceptor(interceptor);
  }, [registerPanelInterceptor, shouldIntercept]);

  const wrap = (result: SubmitPanelResult) => {
    resolveRef.current?.(result);
    payloadRef.current = null;
    setIsIntercepting(false);
  };

  return {
    isIntercepting,
    payloadRef,
    payload: payloadRef.current,
    confirm: () => wrap({ type: 'confirm' }),
    cancel: () => wrap({ type: 'cancel' }),
    replace: (panel: Panel) => {
      if (!payloadRef.current) return;

      wrap({ type: 'replace', payload: { ...payloadRef.current, panel } });
    }
  };
}
